/* eslint-disable @typescript-eslint/no-magic-numbers */
import Analytics from 'electron-google-analytics4';
import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { getCleverTapInstance } from '@/config/clevertap';
import app from '@/config/firebase';
import Constants from '@/Constants';
import logger from '@/logger';
import { nudgeSignals, reportNudgeEvent } from '@/pages/Nudging/nudgingEngine';
import useStore from '@/store';
import { getSessionItem, isDesktop, isLinux, setSessionItem } from '@/utils/helper';

export type AnalyticsEvent = {
  name: string;
  label?: string;
  category?: string;
  value?: number | string;
  params?: Record<string, string | number | boolean | null | undefined>;
  sendToCleverTap?: boolean;
  sendToNudgeEngine?: boolean;
};

type AdminDetails = {
  programCode?: string;
  centerCode?: string;
};

const useAnalytics = () => {
  const { isUserInitialized, setIsUserInitialized } = useStore();
  const webAnalytics = useMemo(() => (isDesktop() ? undefined : getAnalytics(app)), []);

  const gaSessionId = useMemo<string>(() => {
    if (getSessionItem('gaSessionId')) {
      return getSessionItem('gaSessionId') as string;
    }
    const newGaSessionId = uuidv4();
    setSessionItem('gaSessionId', newGaSessionId);
    return newGaSessionId;
  }, []);
  const desktopAnalytics = useMemo(
    () =>
      isDesktop()
        ? new Analytics(
            isLinux() ? Constants.UBUNTU_GA_MEASUREMENT_ID : Constants.DESKTOP_GA_MEASUREMENT_ID,
            isLinux() ? Constants.UBUNTU_GA_API_SECRET_KEY : Constants.DESKTOP_GA_API_SECRET_KEY,
            localStorage.getItem('device-id') ?? '',
            gaSessionId,
          )
        : undefined,
    [gaSessionId],
  );

  useEffect(() => {
    if (!isUserInitialized) {
      const adminDetails = JSON.parse(localStorage.getItem('adminDetails') || '{}') as AdminDetails;
      if (webAnalytics && !isDesktop()) {
        setUserId(webAnalytics, localStorage.getItem('device-id') || '');
        setUserProperties(webAnalytics, {
          program_code: adminDetails?.programCode ?? '',
          center_code: adminDetails?.centerCode ?? '',
          version_code: Constants.APP_VERSION,
        });
        setIsUserInitialized(true);
      } else {
        const analyticsChain = desktopAnalytics?.setParams({ engagement_time_msec: 1 });
        analyticsChain?.setUserProperties({
          program_code: adminDetails?.programCode ?? '',
          center_code: adminDetails?.centerCode ?? '',
          version_code: Constants.APP_VERSION,
        });
        setIsUserInitialized(true);
      }
    }
  }, [isUserInitialized, setIsUserInitialized, webAnalytics, desktopAnalytics]);

  const trackEvent = useCallback(
    (event: AnalyticsEvent) => {
      try {
        const {
          name,
          label,
          category,
          value: eventValue,
          params,
          sendToCleverTap = true,
          sendToNudgeEngine = true,
        } = event;
        const eventParameters = {
          ...(category && { category }),
          ...(label && { label }),
          ...(eventValue && { value: eventValue }),
          ...(params &&
            Object.fromEntries(
              Object.entries(params).filter(
                ([, parameterValue]) => parameterValue !== null && parameterValue !== undefined,
              ),
            )),
        };

        if (isDesktop() && desktopAnalytics) {
          let analyticsChain = desktopAnalytics.setParams({ engagement_time_msec: 1 });

          for (const [key, value] of Object.entries(eventParameters)) {
            analyticsChain = analyticsChain.set(key, value);
          }

          void analyticsChain.event(name);
        } else if (webAnalytics) {
          logEvent(
            webAnalytics,
            name,
            Object.keys(eventParameters).length > 0 ? eventParameters : undefined,
          );
        }

        if (sendToCleverTap) {
          try {
            const clevertap = getCleverTapInstance();
            if (clevertap && Object.keys(eventParameters).length > 0) {
              clevertap.event.push(name, eventParameters);
            } else if (clevertap) {
              clevertap.event.push(name);
            }
          } catch (ctError) {
            logger.warn('CleverTap event skipped:', name, ctError);
          }
        }

        // Mirror every event to the nudging engine (port of AnalyticsGatewayImpl.sendEvent).
        if (sendToNudgeEngine) {
          if (name === Constants.NUDGE_EVENT_NAMES.HINT_CLICKED) {
            nudgeSignals.onHintClicked();
          }
          void reportNudgeEvent({ name, params: eventParameters });
        }
      } catch (error) {
        logger.error('Error tracking analytics event:', error);
      }
    },
    [webAnalytics, desktopAnalytics],
  );

  return { trackEvent };
};

export default useAnalytics;
